import { CompanyLogoCircle } from '@/components/company-logo-circle';
import { CompanyLogoDialog, type CompanyLogoDialogProps } from '@/components/company-logo-dialog';
import { cn } from '@/lib/utils';
import type { CompanyLogo } from '@mss_creator/shared';
import { useState } from 'react';

export interface CompanyLogoFieldProps
  extends Pick<CompanyLogoDialogProps, 'onImportUrl' | 'onSave' | 'onRemove'> {
  /** The company's logo as it is now (or, on the creation form, as it was chosen), or `null`. */
  logo: CompanyLogo | null;
  /** Company name — what the circle is called to assistive technology. */
  companyName: string;
}

/**
 * The 80 px logo circle of « Paramètres entreprise » and of the creation form
 * (issue #20, planche 06). The circle *is* the control: a click opens the logo
 * editor, which does the importing, the framing and the removing — this
 * component only draws the circle and hands the editor's three actions to
 * whoever owns the logo (the settings page saves it at once; the creation form
 * keeps it until « Créer »).
 *
 * - no logo → dashed circle with a « + », the « empty slot » vocabulary of
 *   `NewMatrixCard`. Deliberately unlike `CompanyCard`, which fills the empty
 *   circle with the noyau colour: that one is *display* (an identity cue you
 *   scan a grid by), this one is *editing*. Do not « unify » them.
 * - logo → the framed image, with « Modifier » over it on hover and focus.
 */
export function CompanyLogoField({
  logo,
  companyName,
  onImportUrl,
  onSave,
  onRemove,
}: CompanyLogoFieldProps) {
  const [open, setOpen] = useState(false);
  // An image that cannot be loaded (a dead old URL) reads as no logo, so the
  // circle invites a new one instead of showing a broken-image glyph.
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  const shown = logo !== null && brokenSrc !== logo.src ? logo : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={shown ? 'Modifier le logo' : 'Ajouter un logo'}
        className={cn(
          'group relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit focus-visible:ring-offset-2',
          !shown && 'border-2 border-gray-300 border-dashed hover:border-bleu-nuit',
        )}
      >
        {shown ? (
          <CompanyLogoCircle
            logo={shown}
            alt={companyName}
            className="size-full"
            onError={() => setBrokenSrc(shown.src)}
          />
        ) : (
          <span aria-hidden="true" className="text-gray-400 text-h3 group-hover:text-bleu-nuit">
            +
          </span>
        )}
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-(--backdrop) font-semibold text-blanc text-micro opacity-0 transition-opacity',
            'group-hover:opacity-100 group-focus-visible:opacity-100',
            !shown && 'hidden',
          )}
        >
          Modifier
        </span>
      </button>
      <CompanyLogoDialog
        open={open}
        onOpenChange={setOpen}
        current={logo}
        onImportUrl={onImportUrl}
        onSave={onSave}
        {...(onRemove ? { onRemove } : {})}
      />
    </>
  );
}
